import { ReactNode } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { NotificationCenter } from "@/components/NotificationCenter";
import { UserHeader } from "@/components/layout/UserHeader";
import { ForcePasswordChange } from "@/components/auth/ForcePasswordChange";
import { SearchTrigger } from "@/components/SearchTrigger";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user needs to change password
  const needsPasswordChange = user?.user_metadata?.force_password_change === true;
  
  if (needsPasswordChange) {
    return <ForcePasswordChange onPasswordChanged={() => window.location.reload()} />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col">
          <UserHeader />
          <header className="h-12 flex items-center justify-between border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
            <SidebarTrigger className="ml-4" />
            <div className="flex-1 max-w-md mx-4">
              <SearchTrigger />
            </div>
            <div className="mr-4 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/support')}
                className="flex items-center gap-2"
              >
                <MessageCircle className="h-5 w-5" />
                <span className="hidden sm:inline">Support</span>
              </Button>
              <NotificationCenter />
            </div>
          </header>
          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}